import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import { formatPrice, STATUS_LABELS, type PropertyStatus, type PropertyType } from "@/types/database";

// Support both legacy format (from mock data) and new database format
interface LegacyProperty {
  id: string;
  name: string;
  location?: string;
  community?: string;
  price: string;
  image: string;
  status?: string;
  type?: string;
}

interface DatabaseProperty {
  id: number;
  name: string;
  location_name: string;
  price_from: number;
  currency?: string;
  hero_image_url: string;
  status: PropertyStatus;
  type: PropertyType;
}

type PropertyInput = LegacyProperty | DatabaseProperty;

interface PropertyCardProps {
  property: PropertyInput;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isHighlighted?: boolean;
}

// Type guard to check if property is database format
function isDatabaseProperty(property: PropertyInput): property is DatabaseProperty {
  return 'price_from' in property && 'hero_image_url' in property;
}

export function PropertyCard({ property, className, onMouseEnter, onMouseLeave, isHighlighted }: PropertyCardProps) {
  // Normalize property data
  const id = String(property.id);
  const name = property.name;
  const image = isDatabaseProperty(property) ? property.hero_image_url : property.image;
  const price = isDatabaseProperty(property) 
    ? formatPrice(property.price_from, property.currency) 
    : property.price;
  const location = isDatabaseProperty(property) 
    ? property.location_name 
    : (property.location ?? property.community ?? "");
  const status = isDatabaseProperty(property) 
    ? STATUS_LABELS[property.status] 
    : (property.status ?? property.type);

  return (
    <div
      className={cn(
        "rounded-xl transition-all duration-200"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Link href={`/properties/${id}`} className={cn("group cursor-pointer block", className)}>
        {/* Image Container - Aspect 16/10, rounded-xl */}
        <div className="relative aspect-[16/10] overflow-hidden bg-secondary/10 mb-6 rounded-xl shadow-xl group-hover:shadow-2xl transition-all duration-300">
          <Image
            src={image}
            alt={name}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            className="object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105 rounded-xl"
          />

          {/* Hover Overlay with Arrow */}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white transform scale-90 group-hover:scale-100 transition-transform duration-500">
              <ArrowUpRight className="w-8 h-8" />
            </div>
          </div>

          {/* Status Badge */}
          {status && (
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1 bg-white/90 backdrop-blur-md text-[10px] uppercase tracking-widest font-bold border border-black/5 text-primary rounded-xl">
                {status}
              </span>
            </div>
          )}
        </div>

        {/* Content Below */}
        <div className="flex justify-between items-start border-t border-primary/10 pt-6">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl mb-1 group-hover:text-secondary transition-colors text-primary">
              {name}
            </h3>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              {location}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium mb-1 text-primary">{price}</p>
            <p className="text-xs text-muted-foreground">Est. 2027</p>
          </div>
        </div>
      </Link>
    </div>
  );
}
